/* eslint-disable eqeqeq */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AccessoryFormApi } from './useAccessoryForm';
import type { MountingDistances } from './mountingMath';
import { formatLegacyNumber, resolveBoundedCommit } from '../formUtils';

/**
 * Phase 6e2b-1 — THE base mounting popup (§51.4 / parity 333):
 * base_classes/mounting_view.js (349 lines) + accessories_mounting_base.tmpl
 * + tmpl/for_base_classes/mounting_popup.tmpl (90 lines), all read in full.
 *
 * Mechanics ported verbatim:
 *  - host: `#popup-only` (accessories_mounting_base wrapper:
 *    .popup-wrapper.location-popup > .popup-content.popup-content-animation);
 *  - plane radio grid from cms_accessory.mounting_plane, EXCLUDING
 *    `internal_wall_vertical` (tmpl :14); `>= 'wall_'` side styling;
 *  - 6 dist-inputs (.dist-input) → mountingModel.set, LOCAL until Done (QA
 *    batch 7 follow-up: the TYPE popups this replaces — light/jbox/vfd/… —
 *    all did bare local sets per change); Done's confirm() creates the
 *    position via formView.createPosition('on_cabinet', plane_id) when none,
 *    else recalc_position — position auto-save persists; on_internal_wall
 *    positions early-return: the popup is on_cabinet-only. INLINE (selector)
 *    mode keeps the eager per-change confirm of the legacy base view over
 *    the never-persisting staging adapters;
 *  - `.model_field_popup` (special-parameters slot) accumulate in
 *    __changed_model_fields, applied to the accessory model on confirm/Done;
 *  - open snapshots the mounting state; CANCEL/hide restores it — with
 *    Done-only persistence the server stays untouched on cancel;
 *  - junk classnames `yyyyyyyyyy`/`vvvvvvv` live in the legacy class lists —
 *    replicated;
 *  - `renderInContainer` 830×520 zoom-to-fit selector mode exposed via the
 *    `inlineContainer` prop (UNEXERCISED until 6e2c — accessory selector).
 *
 * Special-parameters slot: per-type LIVE tmpls (keep-list §51.1). For the
 * 6e2b-1 types: jbox/disconnect/enclosure/adp_gage/vfd/dog_house share the
 * casing_mount radio group; light/starter have none.
 */

const POPUP_NATIVE_WIDTH = 830;
const POPUP_NATIVE_HEIGHT = 520;

const DIST_FIELDS: Array<{ name: keyof MountingDistances; cls: string; junk?: string }> = [
  { name: 'distance_to_floor', cls: 'dist-to-floor' },
  { name: 'distance_to_roof', cls: 'dist-to-roof' },
  { name: 'distance_to_wall_left', cls: 'dist-to-wall_left' },
  { name: 'distance_to_wall_right', cls: 'dist-to-wall_right' },
  { name: 'distance_to_wall_upstream', cls: 'dist-to-wall_upstream' },
  { name: 'distance_to_wall_downstream', cls: 'dist-to-wall_downstream' },
];

/**
 * ★ THE POPUP's DISPLAY FORMATTER, which React never ran.
 *
 * `mounting_view.js:203-210` closes every `__render_popup()` with a sweep that
 * hands EVERY `input,span` carrying `data-formatter_type="decimal"` to
 * `fn.uni_tools.bindFormatter` (`universal_fn/formatter.js`). Both concrete
 * binders format immediately on construction — `NumericInputFormatter`
 * `initialSetup` calls `setValue(element.value)` (:227) and
 * `NumericSpanFormatter` calls `setValue(element.innerText)` (:277) — so the
 * drawing paints `100.00` / `0.00`, not `100` / `0`. The React port carried the
 * `data-formatter_type` attributes over but nothing consumed them, which is the
 * "airway width = 100 where dev shows 100.00" half of the QA report.
 *
 * `formatLegacyNumber(v, 0.01)` is the existing faithful port of the same
 * 2 dp + thousands-separator encoding (see `SelectorNumberInput`), and it
 * returns '' for null/'' — matching `encode()`'s `decodedValue == null` guard
 * and `NumericSpanFormatter`'s `if (value)` skip, so an empty box stays empty.
 */
const fix = (v: unknown): string => formatLegacyNumber(v, 0.01);

/**
 * The inverse of `fix` for the commit path: `fix` may have inserted thousands
 * separators, and a bare `parseFloat('1,234.00')` would commit `1`. Legacy's
 * `decode()` (`universal_fn/formatter.js:99-125`) strips the separators the same
 * way before handing the number back.
 */
const unfix = (s: string): number => Number.parseFloat(s.replace(/,/g, ''));

/**
 * `cls` arrives with the `non-clickable` conditional ALREADY resolved into it
 * (see `distClassFor` — one of the six is whitespace-sensitive), so this
 * component must not append the token itself. `nonClickable` here means only
 * "the token actually landed", i.e. whether the input is locked.
 */
const DistInput = ({
  name,
  cls,
  value,
  nonClickable,
  onCommit,
  min,
}: {
  name: string;
  /** the FULL trailing class list, built by `distClassFor` — see its comment;
   * `non-clickable` is already in here (or deliberately glued to the junk token
   * and therefore not), so it must not be appended a second time. */
  cls: string;
  value: unknown;
  /** whether `non-clickable` actually landed as its own class. */
  nonClickable: boolean;
  onCommit: (name: string, value: number) => void;
  /**
   * Enforced lower bound — the access door's To Floor minimum on
   * `distance_to_floor` (see `accessDoorFloorClearance`). `distancesToPosition`
   * maps that box straight onto the position's `y`, so the Data Bar's To Floor
   * and this one are the same number and get the same bound; without it the
   * popup saved below the minimum and the load-time correction moved the door
   * afterwards, with nothing on screen explaining why. It is computed from the
   * plane STAGED in this popup, not the one the door is saved on — see
   * `floorMinFor`.
   */
  min?: number | null;
}) => {
  /** Shared with `complexMounting`'s `PositionInput` — see `resolveBoundedCommit`. */
  const commit = (el: HTMLInputElement) => {
    if (nonClickable) return;
    const next = resolveBoundedCommit(el, fix(value), min);
    if (next != null) onCommit(name, unfix(next));
  };
  return (
    <input
      data-formatter_type="decimal"
      className={`dist-input ${cls}`}
      data-step="0.01"
      data-min={min != null ? String(min) : '0'}
      type="text"
      data-type="number"
      autoComplete="off"
      name={name}
      key={`${name}_${fix(value)}`}
      defaultValue={fix(value)}
      readOnly={nonClickable}
      onBlur={(e) => commit(e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit(e.currentTarget);
      }}
    />
  );
};

export interface SpecialParametersProps {
  api: AccessoryFormApi;
  stageModelField: (name: string, value: unknown) => void;
  /** staged overrides (mounting_view __changed_model_fields preview). */
  staged: Record<string, unknown>;
}

/** The shared casing_mount radio group (the 18-line special tmpl family). */
export const CasingMountSpecialParameters = ({
  api,
  stageModelField,
  staged,
}: SpecialParametersProps) => {
  const list =
    (api.cmsAccessory?.casing_mount as Array<Record<string, unknown>> | undefined) ?? [];
  const current = staged.casing_mount_id ?? api.model?.get('casing_mount_id');
  return (
    <ul>
      <li className="special-parameter-casing_mount">
        <span className="label">Casing Mount</span>
        <div>
          {list.map((row) => (
            <label className="radio" key={String(row.id)}>
              <input
                type="radio"
                name="casing_mount_id"
                value={String(row.id)}
                checked={current == row.id}
                className="model_field_popup"
                onChange={(e) => stageModelField('casing_mount_id', parseInt(e.target.value, 10))}
              />{' '}
              {/* §JSX-WS — mounting_popup_special_parameters.tmpl:13-14
                  newlines between the radio and `{{name}}`; see
                  CasingMountRow in LouverForm.tsx. */}
              {String(row.name ?? '')}
            </label>
          ))}
        </div>
      </li>
    </ul>
  );
};

const AccessoryMountingPopup = ({
  api,
  typeName,
  typeValue,
  SpecialParameters,
  inlineContainer,
  floorMinFor,
}: {
  api: AccessoryFormApi;
  /** formView.type.replace('_',' ') (tmpl typeName). */
  typeName: string;
  typeValue: string;
  SpecialParameters?: (props: SpecialParametersProps) => JSX.Element;
  /**
   * 6e2c renderInContainer mode (mounting_view.js:122-149): render inline
   * into this element at 830×520 native size, zoom-to-fit. UNEXERCISED in
   * 6e2b-1 — the accessory selector cut consumes it.
   */
  inlineContainer?: HTMLElement | null;
  /**
   * Lower bound for the `distance_to_floor` box — access door only.
   *
   * A FUNCTION of the plane, not a number, because the popup stages a plane
   * change locally: `onPlanePick` only does `mounting.set(...)`, and the
   * position is written on Done (`confirm` → `recalc_position`). The saved
   * plane's minimum would be the wrong bound for as long as the popup is open —
   * on an end-wall door (20) switched to a side wall (15) it would snap a legal
   * 17 up to 20 and save that. `model.clearances` carries both wall pairs
   * whatever the plane, so the bound follows the picked wall.
   */
  floorMinFor?: (stagedPlaneValue: string | null) => number | null;
}) => {
  const { mounting, position, model } = api;
  const [staged, setStaged] = useState<Record<string, unknown>>({});
  // open snapshot (__saveCurrentState) — restored on hide (CANCEL semantics);
  // positions already saved through __confirm stay saved (Q16).
  const savedState = useRef<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (api.popupOpen) {
      savedState.current = mounting ? mounting.toJSON() : null;
      setStaged({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.popupOpen]);

  const host = useMemo(() => document.getElementById('popup-only'), []);

  // zoom-to-fit (inline mode) — measured via ResizeObserver + useLayoutEffect
  // rather than computed inline during render. The host div is
  // `display:none` while its tab isn't active, and this component first
  // mounts in the SAME commit that flips it to `display:block` — reading
  // offsetWidth/offsetHeight during render still sees the PRE-commit
  // (0×0, display:none) box, so a render-time `zoom` comes out 0, an invalid
  // CSS zoom value browsers fall back to 1 (native 830×520, i.e. "bigger")
  // for one frame — the flash before it snaps to normal. useLayoutEffect
  // runs after the commit (display:block already applied) and flushes its
  // setState before paint, so that fallback frame never renders. The
  // observer also keeps zoom correct across the zoom-in/zoom-out toggle's
  // CSS width/height transition, without legacy's setTimeout re-measure hack.
  const [zoom, setZoom] = useState(1);
  useLayoutEffect(() => {
    if (!inlineContainer) return undefined;
    const update = () => {
      const zoomX = inlineContainer.offsetWidth / POPUP_NATIVE_WIDTH;
      const zoomY = inlineContainer.offsetHeight / POPUP_NATIVE_HEIGHT;
      const next = Math.min(zoomX, zoomY, 1);
      if (next > 0) setZoom(next);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(inlineContainer);
    return () => ro.disconnect();
  }, [inlineContainer]);

  // Esc = hide (CANCEL semantics), matching the legacy global handler that
  // closed this `.location-popup` wrapper WITHOUT clicking Done
  // (main.js:334-356 close-regardless branch). The popup has no cancel button
  // for usePopupKeys to match (tmpl parity — Done only), so it owns its Esc,
  // like InsertSectionPopup does; the topmost filter mirrors usePopupKeys,
  // incl. its `.component-selector-popup` exemption.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const hideRef = useRef<(() => void) | null>(null);
  const escActive = !inlineContainer && api.popupOpen;
  useEffect(() => {
    if (!escActive) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const active = Array.from(
        document.querySelectorAll<HTMLElement>('.popup-wrapper.active')
      ).filter((el) => !el.classList.contains('component-selector-popup'));
      if (active[active.length - 1] !== wrapperRef.current) return;
      e.preventDefault();
      hideRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [escActive]);

  if (!mounting || !model) return null;
  if (!api.popupOpen && !inlineContainer) return null;

  const mountingJSON = mounting.toJSON() as Record<string, unknown>;
  const modelJSON = { ...model.toJSON(), ...staged };
  const positionJSON = position?.toJSON() ?? {};
  const planeValue = mounting.plane_value;
  // tmpl :187 — left-coordinate planes.
  const isY =
    planeValue === 'floor' ||
    planeValue === 'roof' ||
    planeValue === 'wall_upstream' ||
    planeValue === 'wall_downstream';
  const hasFloor = !!api.mountingPlanes.find((p) => p.value === 'floor');
  const hasRoof = !!api.mountingPlanes.find((p) => p.value === 'roof');

  const stageModelField = (name: string, value: unknown) => {
    model.markUserAction();
    setStaged((prev) => ({ ...prev, [name]: value }));
  };

  // __confirm (mounting_view.js:302-324).
  const confirm = (after?: () => void) => {
    model.markUserAction();
    position?.markUserAction();
    if (!mounting.position) {
      if (mounting.plane_value && mounting.plane_id) {
        void api.createPosition('on_cabinet', mounting.plane_id).then(() => after?.());
        applyStagedToModel();
        return;
      }
    } else {
      mounting.recalc_position();
      if (position?.mountingType === 'on_internal_wall') return;
    }
    applyStagedToModel();
    after?.();
  };

  const applyStagedToModel = () => {
    setStaged((prev) => {
      if (Object.keys(prev).length) model.set(prev);
      return prev;
    });
  };

  // Per-change edits are LOCAL-ONLY in popup mode (QA batch 7 follow-up):
  // every legacy TYPE popup's changePlane/changeDistance is a bare
  // model_mounting.set (+ recalc_output_fields, which runs inside
  // mounting.set) — e.g. light/view/mounting_popup.js:84-106 — and the server
  // write (recalc_position → position auto-PATCH / createPosition) lives
  // exclusively in Done's confirm(), so Esc/cancel leaves the server
  // untouched. INLINE (selector) mode keeps the per-change confirm(): its
  // legacy host was the eager base mounting_view (mounting_view.js:238/250)
  // and its position is a staging adapter (accessoryStagingMounting — zero
  // network); the selector reads the staged position continuously.
  const onPlanePick = (value: string) => {
    const plane = api.mountingPlanes.find((p) => p.value === value);
    if (!plane) return;
    mounting.set({ plane_id: plane.id, plane_value: String(plane.value ?? '') });
    if (inlineContainer) confirm();
  };

  const onDistCommit = (name: string, value: number) => {
    mounting.set({ [name]: value } as Partial<MountingDistances>);
    if (inlineContainer) confirm();
  };

  // __done: confirm + close (popup mode).
  const done = () => {
    confirm();
    if (!inlineContainer) api.closeMountingPopup();
  };

  // hide (outside-close — CANCEL semantics): restore the mounting snapshot.
  const hide = () => {
    if (savedState.current) {
      const s = savedState.current as Record<string, unknown> & Partial<MountingDistances>;
      mounting.set({
        plane_id: (s.plane_id as number | null) ?? null,
        plane_value: (s.plane_value as string | null) ?? null,
        distance_to_floor: Number(s.distance_to_floor) || 0,
        distance_to_roof: Number(s.distance_to_roof) || 0,
        distance_to_wall_left: Number(s.distance_to_wall_left) || 0,
        distance_to_wall_right: Number(s.distance_to_wall_right) || 0,
        distance_to_wall_upstream: Number(s.distance_to_wall_upstream) || 0,
        distance_to_wall_downstream: Number(s.distance_to_wall_downstream) || 0,
      });
    }
    api.closeMountingPopup();
  };
  hideRef.current = hide;

  const defaultPosX = !!positionJSON.default_pos_x;
  const defaultPosY = !!positionJSON.default_pos_y;
  const nonClickableFor = (field: string): boolean => {
    // `for_base_classes/mounting_popup.tmpl:42-55` default_pos gating per input.
    // This is the template EVERY mounting drawing uses, the rating dialog's
    // Mounting tab included — `view/base_classes/mounting_view.js:16` requires it
    // unconditionally, and `renderInContainer` (:122) is the selector's path.
    // (The per-accessory `accessories/<type>/tmpl/mounting_popup.tmpl` files
    // exist but are NOT what these six inputs come from.)
    //
    // What this flag does is not "grey out": `ahu/styles.css:11137` is
    //   `.position .selected-position input.dist-input.non-clickable:empty { display: none }`
    // and `:empty` is always true of an `<input>` — a void element can have no
    // children — so the selector reduces to "hide it". That is how the drawing
    // ends up showing ONE dimension rather than four. See `distClassFor` for the
    // one input where legacy's own typo stops it applying.
    if (field === 'distance_to_floor' || field === 'distance_to_roof') return defaultPosY;
    if (field === 'distance_to_wall_left') return isY ? defaultPosY : defaultPosX;
    if (field === 'distance_to_wall_upstream') {
      return planeValue === 'floor' || planeValue === 'roof' ? defaultPosX : defaultPosY;
    }
    return defaultPosX; // right / downstream
  };

  /**
   * ★ THE WHITESPACE IS LOAD-BEARING. `styles.css:11137` hides a distance box
   * with `.position .selected-position input.dist-input.non-clickable:empty
   * { display: none }` — and an `<input>` is a void element, so `:empty` always
   * matches; the rule is really just "`.non-clickable` → `display:none`". With
   * Auto-Position on, that hides five of the six boxes.
   *
   * The sixth survives on a TYPO. `tmpl/for_base_classes/mounting_popup.tmpl:45`
   * (the `is_y` branch, i.e. floor/roof/wall_upstream/wall_downstream) writes
   *
   *     class="dist-input dist-to-wall_left  yyyyyyyyyy{{#if position.default_pos_y}}non-clickable{{/if}}"
   *                                                   ^ no space
   *
   * so the token comes out as the single class `yyyyyyyyyynon-clickable`,
   * `.non-clickable` never matches, and legacy leaves that one box VISIBLE and
   * editable. Its sibling on :47 (the `else` branch) has the space and behaves
   * normally. That lone visible box is the `0.00` QA saw beside the inner cube
   * on dev and not on dev3: this port had emitted the junk classes but joined
   * `non-clickable` with a proper space, so React hid all six.
   *
   * Reproduced verbatim rather than "fixed" — the box is a real control on dev
   * and removing it is a product decision, not a port detail.
   */
  const distClassFor = (field: string, nc: boolean): { cls: string; locked: boolean } => {
    const base = DIST_FIELDS.find((f) => f.name === field)!.cls;
    if (field === 'distance_to_wall_left') {
      return isY
        ? { cls: `${base}  yyyyyyyyyy${nc ? 'non-clickable' : ''}`, locked: false }
        : { cls: `${base} vvvvvvv ${nc ? 'non-clickable' : ''}`, locked: nc };
    }
    return { cls: `${base}${nc ? ' non-clickable' : ''}`, locked: nc };
  };
  const distValueFor = (field: keyof MountingDistances): unknown => {
    // tmpl mixes mounting + MODEL sources: roof/right/downstream read the
    // accessory model's distance_to_* (:43/:49/:55), the rest the mounting.
    if (field === 'distance_to_roof') return modelJSON.distance_to_roof ?? mountingJSON[field];
    if (field === 'distance_to_wall_right')
      return modelJSON.distance_to_right_wall ?? mountingJSON[field];
    if (field === 'distance_to_wall_downstream')
      return modelJSON.distance_to_downstream_wall ?? mountingJSON[field];
    return mountingJSON[field];
  };

  const body = (
    <div className="">
      <h2 className="popup-heading">Mounting</h2>
      <div className="popup-content-part accss-section">
        <span className="accss-name">{typeName}</span>
        <span className="section-tag">{String(modelJSON.tag ?? '')}</span>
      </div>
      <form>
        <div
          className={`popup-content-part two-cols-popup-content${
            modelJSON.hood_id ? ' mounted-on-hood' : ''
          }`}
        >
          <div className="plane location-main-item">
            <div className="left-div">
              <h2>
                Select Wall
                {hasFloor ? (hasRoof ? ', Floor' : ' or Floor') : ''}
                {hasRoof ? ' or Roof' : ''}
              </h2>
              <div className="plane-content">
                {api.mountingPlanes
                  .filter((p) => p.value !== 'internal_wall_vertical')
                  .map((p) => (
                    <label className={String(p.value ?? '')} key={String(p.id)}>
                      <input
                        type="radio"
                        className="mounting_plane"
                        data-position="on_cabinet"
                        name="plane"
                        value={String(p.value ?? '')}
                        checked={mounting.plane_id == p.id}
                        data-plane={String(p.value ?? '')}
                        onChange={() => onPlanePick(String(p.value ?? ''))}
                      />
                      {String(p.value ?? '') >= 'wall_' ? (
                        <span className="side">
                          <span>{String(p.name ?? '')}</span>
                        </span>
                      ) : (
                        <span>{String(p.name ?? '')}</span>
                      )}
                    </label>
                  ))}
                <span className="airway-sizes-note right-note airway-width">
                  airway width = <span data-formatter_type="decimal">{fix(api.awDims.aw_width)}</span>
                </span>
                <span className="airway-sizes-note bottom-note airway-length">
                  airway length = <span data-formatter_type="decimal">{fix(api.awDims.aw_length)}</span>
                </span>
                <h5 className="popup-note">
                  Plan View
                  <span>
                    Air Direction is left to right{' '}
                    <button
                      type="button"
                      className="icon-airflow-direction-icon btn-tunnel-airflow-direction left-to-right"
                    />
                  </span>
                </h5>
              </div>
            </div>
          </div>
          <div
            className={`position location-main-item${
              mounting.plane_value ? ` ${mounting.plane_value}-plane` : ''
            }`}
          >
            <div>
              <h2>
                Selected Plane: <span className="selected-plane" />
              </h2>
              <span className="invalid-field-note">Check fields</span>
              <div className="selected-position">
                <div className={`content ${typeValue}`}>
                  <span className="accessory-size accessory-size-width" data-formatter_type="decimal">
                    {fix(modelJSON.width_calc)}
                  </span>
                  <span className="accessory-size accessory-size-height" data-formatter_type="decimal">
                    {fix(modelJSON.height_calc)}
                  </span>
                </div>
                {DIST_FIELDS.map((f) => {
                  const { cls, locked } = distClassFor(f.name, nonClickableFor(f.name));
                  return (
                    <DistInput
                      key={f.name}
                      name={f.name}
                      cls={cls}
                      value={distValueFor(f.name)}
                      nonClickable={locked}
                      onCommit={onDistCommit}
                      min={
                        f.name === 'distance_to_floor' && floorMinFor
                          ? floorMinFor(planeValue)
                          : null
                      }
                    />
                  );
                })}
                <span className="plane-in-view floor">Floor</span>
                <span className="plane-in-view roof">Roof</span>
                <span className="plane-in-view wall_left">Wall - Left</span>
                <span className="plane-in-view wall_right">Wall - Right</span>
                <span className="plane-in-view wall_upstream">Wall - Upstream</span>
                <span className="plane-in-view wall_downstream">Wall - Downstream</span>
              </div>
              <span className="airway-sizes-note airway-width">
                airway width = <span data-formatter_type="decimal">{fix(api.awDims.aw_width)}</span>
              </span>
              <span className="airway-sizes-note airway-height">
                airway height = <span data-formatter_type="decimal">{fix(api.awDims.aw_height)}</span>
              </span>
              <span className="airway-sizes-note airway-length">
                airway length = <span data-formatter_type="decimal">{fix(api.awDims.aw_length)}</span>
              </span>
              <h5 className="popup-note view-type">
                <span className="view-description" />
              </h5>
            </div>
          </div>
        </div>
        {SpecialParameters && (
          <div className="special-parameters">
            <h2>Special Parameters</h2>
            <div className="special-parameters-container">
              <SpecialParameters api={api} stageModelField={stageModelField} staged={staged} />
            </div>
          </div>
        )}
      </form>
      <div className="popup-control-buttons">
        <button className="btn" name="done" autoFocus onClick={done} type="button">
          Done
        </button>
      </div>
    </div>
  );

  if (inlineContainer) {
    // renderInContainer zoom-to-fit — `zoom` comes from the ResizeObserver
    // state above, not a fresh render-time measurement (see its comment).
    return createPortal(
      <div className="popup-wrapper location-popup is-inline active">
        <div
          className="popup-content popup-content-animation"
          style={{ width: POPUP_NATIVE_WIDTH, height: POPUP_NATIVE_HEIGHT, zoom } as React.CSSProperties}
        >
          {body}
        </div>
      </div>,
      inlineContainer
    );
  }

  if (!host) return null;
  return createPortal(
    <div ref={wrapperRef} className="popup-wrapper location-popup active" onClick={(e) => {
      // outside click = hide (CANCEL semantics — popup chrome behavior).
      if (e.target === e.currentTarget) hide();
    }}>
      <div className="popup-content popup-content-animation">{body}</div>
    </div>,
    host
  );
};

export default AccessoryMountingPopup;
